import React, { useState, useEffect, useCallback } from 'react';
import { getMe, getBuildings } from './services/api';
import Login from './pages/Login';
import Register from './pages/Register';
import Setup from './pages/Setup';
import Dashboard from './pages/Dashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LandingPage from './pages/LandingPage';
// ✅ NEW: Admin imports
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import useIdleLogout from './hooks/useIdleLogout';

function getPageFromUrl() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  // ✅ NEW: Check for admin routes
  if (path === '/admin/login') return 'admin-login';
  if (path === '/admin') return 'admin-dashboard';
  if (path === '/login') return 'login';
  if (path === '/register' || params.get('ref')) return 'register';
  return 'landing';
}

function App() {
  const [user, setUser] = useState(null);
  // ✅ NEW: Admin user state
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [page, setPage] = useState(getPageFromUrl);
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(60);

  function navigateTo(newPage) {
    const paths = { 
      landing: '/', 
      login: '/login', 
      register: '/register',
      // ✅ NEW: Admin paths
      'admin-login': '/admin/login',
      'admin-dashboard': '/admin'
    };
    window.history.pushState({ page: newPage }, '', paths[newPage] || '/');
    setPage(newPage);
  }

  useEffect(() => {
    function handlePopState(e) {
      if (e.state && e.state.page) {
        setPage(e.state.page);
      } else {
        setPage(getPageFromUrl());
      }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ✅ Check for landlord session
  useEffect(() => {
    const token = localStorage.getItem('hashvi_token');
    if (token) {
      getMe()
        .then(res => { setUser(res.data); return getBuildings(); })
        .then(res => { setNeedsSetup(res.data.length === 0); })
        .catch(() => { localStorage.removeItem('hashvi_token'); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ✅ NEW: Check for admin session
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('admin_user');
    if (adminToken && adminData) {
      try {
        setAdminUser(JSON.parse(adminData));
      } catch (e) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }
    }
  }, []);

  // Countdown ticker while warning toast is visible
  useEffect(() => {
    if (!idleWarning) return;
    setIdleCountdown(60);
    const tick = setInterval(() => {
      setIdleCountdown(prev => {
        if (prev <= 1) { clearInterval(tick); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [idleWarning]);

  const handleLogin = async (userData, token) => {
    localStorage.setItem('hashvi_token', token);
    setUser(userData);
    try {
      const res = await getBuildings();
      setNeedsSetup(res.data.length === 0);
    } catch (e) {
      setNeedsSetup(false);
    }
  };

  const handleRegister = async (userData, token) => {
    localStorage.setItem('hashvi_token', token);
    setUser(userData);
    setNeedsSetup(true);
  };

  const handleSetupComplete = () => setNeedsSetup(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('hashvi_token');
    setUser(null);
    setIdleWarning(false);
    setNeedsSetup(false);
    setPage('landing');
    window.history.pushState({ page: 'landing' }, '', '/');
  }, []);

  // ✅ NEW: Admin login handler
  const handleAdminLogin = (admin) => {
    setAdminUser(admin);
    navigateTo('admin-dashboard');
  };

  // ✅ NEW: Admin logout handler
  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdminUser(null);
    navigateTo('admin-login');
  };

  const handleIdleWarn = useCallback(() => setIdleWarning(true), []);
  const handleIdleWarnClear = useCallback(() => setIdleWarning(false), []);

  // Hook only fires when a landlord is logged in
  useIdleLogout(
    user ? handleLogout : null,
    user ? handleIdleWarn : null,
    user ? handleIdleWarnClear : null
  );

  if (loading) {
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', height: '100vh',
          color: '#64748b', fontSize: 14
        }}>Loading Hashvi...</div>
      </>
    );
  }

  if (window.location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />;
  }

  return (
    <>
      {/* Idle logout warning toast */}
      {user && idleWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: '#1e293b', color: 'white',
          padding: '14px 24px', borderRadius: 10,
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 16,
          fontSize: 14, fontWeight: 500,
          border: '1px solid rgba(255,255,255,0.1)',
          minWidth: 320
        }}>
          <span style={{ fontSize: 20 }}>⏱</span>
          <span style={{ flex: 1 }}>
            Logging out in{' '}
            <strong style={{ color: idleCountdown <= 15 ? '#f87171' : '#fbbf24' }}>
              {idleCountdown}s
            </strong>
            {' '}due to inactivity.
          </span>
          <button
            onClick={handleIdleWarnClear}
            style={{
              background: '#1a56db', color: 'white',
              border: 'none', borderRadius: 6,
              padding: '6px 14px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Stay Logged In
          </button>
        </div>
      )}

      {/* ✅ NEW: Admin routes */}
      {page === 'admin-login' && !adminUser && (
        <AdminLogin onLoginSuccess={handleAdminLogin} />
      )}
      {page === 'admin-dashboard' && adminUser && (
        <AdminDashboard admin={adminUser} onLogout={handleAdminLogout} />
      )}
      {page === 'admin-dashboard' && !adminUser && (
        // Redirect to admin login if not authenticated
        <AdminLogin onLoginSuccess={handleAdminLogin} />
      )}

      {/* Authenticated landlord routes */}
      {user && needsSetup && (
        <Setup user={user} onComplete={handleSetupComplete} onLogout={handleLogout} />
      )}
      {user && !needsSetup && (
        <Dashboard user={user} onLogout={handleLogout} />
      )}

      {/* Public routes */}
      {!user && page === 'login' && (
        <Login
          onLogin={handleLogin}
          onRegister={() => navigateTo('register')}
          onBack={() => navigateTo('landing')}
        />
      )}
      {!user && page === 'register' && (
        <Register
          onRegister={handleRegister}
          onBack={() => navigateTo('login')}
        />
      )}
      {!user && page === 'landing' && (
        <LandingPage
          onLogin={() => navigateTo('login')}
          onRegister={() => navigateTo('register')}
        />
      )}
    </>
  );
}

export default App;
